/**
 * #933 PR 2b — Settings → Capacity: the assessment schedule.
 *
 * Ted's A1 (Mon–Sat, first slot 10:00, 20-minute stagger, 2 per day) was
 * `configure-assessment-service.ts` run by a Claude session. This form is
 * the same body (`tenant-settings/assessment-schedule.ts`, shared by the
 * route and the script): Save writes exactly the rows the script writes —
 * one DAY rule per weekday carrying the daily cap, one SLOT rule per time —
 * and prunes the rules for days and times the owner no longer wants, so
 * "Tue/Thu only" means Tue/Thu only.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: the preview line ("Mon–Sat · 10:00, 10:20 ·
 *    2 per day") is derived from the current form, so what the owner reads
 *    is what Save writes. The first-slot + stagger fields are a convenience
 *    that expands to the stored `times[]` client-side; an uneven schedule
 *    reads back as explicit times rather than being quietly regularised.
 *  - #2 The next action is obvious: one primary Save, enabled only when the
 *    form differs from what the server holds (or nothing is configured yet);
 *    stopping assessments is a two-tap with its consequence spelled out.
 *  - #5 Never lie about data: Save reports what was written and removed from
 *    the server's own counts; the server's warnings (per-slot caps binding)
 *    and notes (caps that differ by weekday) are shown, not swallowed.
 *  - Five states: loading · error(+retry) · empty (not configured — teaching
 *    copy, form pre-filled with Ted's defaults) · ready · saving.
 *
 * Counter Test: Ted changes the schedule to Tue/Thu on his phone and taps
 * Save; the card says "12 rules removed", and the customer app offers only
 * Tuesdays and Thursdays from the next load.
 *
 * Server contract: GET/PUT/DELETE /api/v2/admin/tenant/assessment-schedule.
 * Read business-config:read:all; write business-config:write:all (owner, manager;
 * a change of price, owner only).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Input, Select, Skeleton } from '@ferro/ui';
import {
  adminTenantCapacityApi,
  type TenantAssessmentSchedule,
  type TenantAssessmentScheduleInput,
  type TenantAssessmentScheduleWritten,
} from '../../lib/api.js';
import {
  assessmentPreviewLine,
  centsToDollarInput,
  describeWeekdays,
  describeWritten,
  dollarInputToCents,
  expandSlotTimes,
  formatDollars,
  hoursInputToMinutes,
  inferSlotPattern,
  intInput,
  minutesToHoursInput,
  parseTimesInput,
  WEEKDAY_SHORT,
  WEEKDAYS_MON_FIRST,
} from './capacity-helpers.js';

interface AssessmentScheduleCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager). Everyone else sees read-only. */
  canEdit: boolean;
  /** #933 D2 — may change the price (owner). Defaults to `canEdit`. */
  canEditPrice?: boolean;
}

type Status = 'loading' | 'error' | 'ready';

export interface ScheduleForm {
  days: number[];
  mode: 'even' | 'times';
  firstSlot: string;
  stagger: string;
  slots: string;
  timesText: string;
  dailyCap: string;
  slotCap: string;
  price: string;
  duration: string;
  /** Hours; blank = no minimum. */
  notice: string;
  /** Days ahead; blank = no limit. */
  window: string;
}

/** Ted's 2026-09-08 decision, the platform's starting point for a new shop. */
export const SCHEDULE_DEFAULTS: ScheduleForm = {
  days: [1, 2, 3, 4, 5, 6],
  mode: 'even',
  firstSlot: '10:00',
  stagger: '20',
  slots: '2',
  timesText: '',
  dailyCap: '2',
  slotCap: '',
  price: '0.00',
  duration: '30',
  notice: '',
  window: '',
};

export function formFromSchedule(s: TenantAssessmentSchedule): ScheduleForm {
  const shared = {
    price: centsToDollarInput(s.priceCents ?? 0),
    duration: String(s.durationMinutes ?? 30),
    notice: minutesToHoursInput(s.minLeadTimeMinutes),
    window: s.maxAdvanceDays === null ? '' : String(s.maxAdvanceDays),
  };
  if (!s.configured) return { ...SCHEDULE_DEFAULTS, ...shared };
  const pattern = inferSlotPattern(s.times);
  return {
    days: [...s.days].sort((a, b) => a - b),
    mode: pattern ? 'even' : 'times',
    firstSlot: pattern?.firstSlot ?? SCHEDULE_DEFAULTS.firstSlot,
    stagger: String(pattern?.staggerMinutes ?? SCHEDULE_DEFAULTS.stagger),
    slots: String(pattern?.slotsPerDay ?? s.times.length),
    timesText: s.times.join(', '),
    dailyCap: s.dailyCap === null ? '' : String(s.dailyCap),
    slotCap: s.slotCap !== null && s.slotCap !== s.dailyCap ? String(s.slotCap) : '',
    ...shared,
  };
}

export function timesFromForm(f: ScheduleForm): string[] | null {
  if (f.mode === 'times') return parseTimesInput(f.timesText);
  return expandSlotTimes(f.firstSlot, Number(f.stagger), Number(f.slots));
}

export function inputFromForm(
  f: ScheduleForm,
  current: TenantAssessmentSchedule,
  canEditPrice = true,
): { ok: true; input: TenantAssessmentScheduleInput } | { ok: false; message: string } {
  const fail = (message: string) => ({ ok: false as const, message });
  if (f.days.length === 0) return fail('Pick at least one day.');
  const times = timesFromForm(f);
  if (!times) {
    return fail(
      f.mode === 'times'
        ? 'Times must be 24-hour HH:MM, separated by commas — for example 10:00, 10:20.'
        : 'Check the first slot (HH:MM), minutes between slots (5–720) and slots per day (1–48). The last slot must start before midnight.',
    );
  }
  const dailyCap = intInput(f.dailyCap, 1, 500);
  if (dailyCap === null || dailyCap === undefined) {
    return fail('Assessments per day must be a whole number between 1 and 500.');
  }
  const slotCap = intInput(f.slotCap, 1, 500);
  if (slotCap === undefined) return fail('Per slot must be a whole number between 1 and 500, or blank for the same as per day.');
  const priceCents = dollarInputToCents(f.price);
  if (priceCents === null) return fail('Enter the price in dollars, for example 45 or 45.50.');
  const durationMinutes = intInput(f.duration, 5, 480);
  if (durationMinutes === null || durationMinutes === undefined) return fail('Duration must be between 5 and 480 minutes.');
  const noticeMinutes = hoursInputToMinutes(f.notice);
  if (noticeMinutes === undefined || (noticeMinutes !== null && noticeMinutes > 60 * 24 * 60)) {
    return fail('Minimum notice must be a number of hours (up to 1440), or blank for none.');
  }
  const windowDays = intInput(f.window, 1, 730);
  if (windowDays === undefined) return fail('Booking window must be between 1 and 730 days, or blank for no limit.');

  const input: TenantAssessmentScheduleInput = {
    days: [...f.days].sort((a, b) => a - b),
    times,
    dailyCap,
    ...(slotCap !== null ? { slotCap } : {}),
    // #933 D2 — omitted = the server keeps the stored price.
    ...(canEditPrice ? { priceCents } : {}),
    durationMinutes,
  };
  // The server's contract: omitted = leave the stored value alone, null =
  // clear it. Send null only when there is a stored value to clear.
  if (noticeMinutes !== null) input.minLeadTimeMinutes = noticeMinutes;
  else if (current.minLeadTimeMinutes !== null) input.minLeadTimeMinutes = null;
  if (windowDays !== null) input.maxAdvanceDays = windowDays;
  else if (current.maxAdvanceDays !== null) input.maxAdvanceDays = null;
  return { ok: true, input };
}

/** Does the form differ from what the server holds? Unconfigured is always "dirty": Save is the point. */
export function formIsDirty(f: ScheduleForm, s: TenantAssessmentSchedule): boolean {
  if (!s.configured) return true;
  const base = formFromSchedule(s);
  const timesOf = (x: ScheduleForm) => timesFromForm(x)?.join(',') ?? `raw:${x.timesText}`;
  const sortedDays = (d: number[]) => [...d].sort((a, b) => a - b).join(',');
  return (
    sortedDays(f.days) !== sortedDays(base.days) ||
    timesOf(f) !== timesOf(base) ||
    f.dailyCap.trim() !== base.dailyCap ||
    f.slotCap.trim() !== base.slotCap ||
    dollarInputToCents(f.price) !== dollarInputToCents(base.price) ||
    f.duration.trim() !== base.duration ||
    (hoursInputToMinutes(f.notice) ?? null) !== (hoursInputToMinutes(base.notice) ?? null) ||
    f.window.trim() !== base.window
  );
}

const MODE_OPTIONS = [
  { value: 'even', label: 'Evenly spaced from a first slot' },
  { value: 'times', label: 'Specific times' },
] as const;

export function AssessmentScheduleCard({
  apiUrl,
  canEdit,
  canEditPrice = canEdit,
}: AssessmentScheduleCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<TenantAssessmentSchedule | null>(null);
  const [form, setForm] = useState<ScheduleForm>(SCHEDULE_DEFAULTS);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState<{ written: TenantAssessmentScheduleWritten; warnings: string[] } | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const apply = useCallback((s: TenantAssessmentSchedule) => {
    setSchedule(s);
    setForm(formFromSchedule(s));
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    setSaved(null);
    const res = await adminTenantCapacityApi.getAssessmentSchedule(apiUrl);
    if (res.error || !res.data) {
      setLoadError(res.error ?? 'Could not load the assessment schedule.');
      setStatus('error');
      return;
    }
    apply(res.data.schedule);
    setStatus('ready');
  }, [apiUrl, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const isDirty = useMemo(() => (schedule ? formIsDirty(form, schedule) : false), [form, schedule]);

  const preview = useMemo(() => {
    const times = timesFromForm(form);
    const dailyCap = intInput(form.dailyCap, 1, 500) ?? null;
    const slotCap = intInput(form.slotCap, 1, 500) ?? null;
    return assessmentPreviewLine({ days: form.days, times, dailyCap, slotCap });
  }, [form]);

  const toggleDay = useCallback((d: number) => {
    setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));
    setSaved(null);
    setSaveError(null);
  }, []);

  const handleSave = useCallback(async () => {
    if (!schedule) return;
    setSaveError(null);
    setSaved(null);
    const parsed = inputFromForm(form, schedule, canEditPrice);
    if (!parsed.ok) {
      setSaveError(parsed.message);
      return;
    }
    setIsSaving(true);
    const res = await adminTenantCapacityApi.saveAssessmentSchedule(apiUrl, parsed.input);
    setIsSaving(false);
    if (res.error || !res.data) {
      setSaveError(res.error ?? 'Could not save the schedule. Please try again.');
      return;
    }
    apply(res.data.schedule);
    setSaved({ written: res.data.written, warnings: res.data.warnings });
  }, [apiUrl, apply, form, schedule]);

  const handleReset = useCallback(() => {
    if (schedule) setForm(formFromSchedule(schedule));
    setSaveError(null);
    setSaved(null);
  }, [schedule]);

  const handleClear = useCallback(async () => {
    setIsClearing(true);
    setSaveError(null);
    setSaved(null);
    const res = await adminTenantCapacityApi.clearAssessmentSchedule(apiUrl);
    setIsClearing(false);
    setConfirmClear(false);
    if (res.error) {
      setSaveError(res.error);
      return;
    }
    await load();
  }, [apiUrl, load]);

  // ── Loading ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <Card title="Assessment schedule">
        <div data-testid="assessment-schedule-loading">
          <Skeleton variant="text" lines={6} />
        </div>
      </Card>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (status === 'error' || !schedule) {
    return (
      <Card title="Assessment schedule">
        <div data-testid="assessment-schedule-error">
          <Alert variant="error">{loadError ?? 'Could not load the assessment schedule.'}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const subtitle =
    'When new dogs book their first-visit assessment, how many you take, and what it costs. Save writes the exact rules the booking engine reads.';

  // ── Read-only (non-owner) ────────────────────────────────────────────
  if (!canEdit) {
    return (
      <Card title="Assessment schedule" subtitle={subtitle}>
        <div className="ferro-settings__card" data-testid="assessment-schedule-readonly">
          {schedule.configured ? (
            <dl className="ferro-settings__readonly">
              <div className="ferro-settings__readonly-row">
                <dt>Days</dt>
                <dd>{describeWeekdays(schedule.days)}</dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Times</dt>
                <dd>{schedule.times.join(', ')}</dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Per day</dt>
                <dd>
                  {schedule.dailyCap ?? 'Differs by weekday'}
                  {schedule.slotCap !== null && schedule.slotCap !== schedule.dailyCap ? (
                    <span className="ferro-muted"> · {schedule.slotCap} per slot</span>
                  ) : null}
                </dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Price</dt>
                <dd>{schedule.priceCents === null ? '—' : formatDollars(schedule.priceCents)}</dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Duration</dt>
                <dd>{schedule.durationMinutes === null ? '—' : `${schedule.durationMinutes} minutes`}</dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Minimum notice</dt>
                <dd>{schedule.minLeadTimeMinutes === null ? 'None' : `${minutesToHoursInput(schedule.minLeadTimeMinutes)} hours`}</dd>
              </div>
              <div className="ferro-settings__readonly-row">
                <dt>Booking window</dt>
                <dd>{schedule.maxAdvanceDays === null ? 'No limit' : `${schedule.maxAdvanceDays} days ahead`}</dd>
              </div>
            </dl>
          ) : (
            <p className="ferro-settings__teaching" data-testid="assessment-schedule-empty">
              Assessments aren&apos;t offered yet. Customers can book daycare and boarding without one until an
              owner or manager sets a schedule.
            </p>
          )}
          {schedule.notes.length > 0 ? (
            <div data-testid="assessment-notes">
              <Alert variant="info">{schedule.notes.join(' ')}</Alert>
            </div>
          ) : null}
          <p className="ferro-muted" data-testid="assessment-readonly-note">
            Only an owner or manager can change the assessment schedule.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Assessment schedule" subtitle={subtitle}>
      <div className="ferro-settings__card" data-testid="assessment-schedule-ready">
        {!schedule.configured ? (
          <p className="ferro-settings__teaching" data-testid="assessment-schedule-empty">
            Assessments aren&apos;t offered yet — customers can book daycare and boarding without one. Pick the
            days and times below and Save; every new dog then books an assessment first.
          </p>
        ) : null}
        {schedule.notes.length > 0 ? (
          <div data-testid="assessment-notes">
            <Alert variant="info">{schedule.notes.join(' ')}</Alert>
          </div>
        ) : null}

        <form
          className="ferro-form ferro-settings__form"
          // noValidate: bounds are explained in our own words; validation
          // runs in inputFromForm() on submit.
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            void handleSave();
          }}
          aria-label="Assessment schedule"
        >
          <fieldset className="ferro-settings__fieldset">
            <legend className="ferro-field__label">Days offered</legend>
            <div className="ferro-settings__daychips" role="group" aria-label="Days offered">
              {WEEKDAYS_MON_FIRST.map((d) => (
                <button
                  key={d}
                  type="button"
                  className="ferro-settings__daychip"
                  aria-pressed={form.days.includes(d)}
                  onClick={() => toggleDay(d)}
                  data-testid={`assessment-day-${d}`}
                >
                  {WEEKDAY_SHORT[d]}
                </button>
              ))}
            </div>
          </fieldset>

          <Select
            name="assessmentMode"
            label="Times"
            value={form.mode}
            onChange={(e) => setForm((f) => ({ ...f, mode: e.target.value as ScheduleForm['mode'] }))}
            options={MODE_OPTIONS}
            data-testid="assessment-mode"
          />
          {form.mode === 'even' ? (
            <div className="ferro-settings__form-grid ferro-settings__form-grid--3">
              <Input
                name="assessmentFirstSlot"
                label="First slot"
                type="time"
                value={form.firstSlot}
                onChange={(e) => setForm((f) => ({ ...f, firstSlot: e.target.value }))}
                data-testid="assessment-first-slot"
              />
              <Input
                name="assessmentStagger"
                label="Minutes between slots"
                type="number"
                inputMode="numeric"
                min={5}
                max={720}
                step={5}
                value={form.stagger}
                onChange={(e) => setForm((f) => ({ ...f, stagger: e.target.value }))}
                data-testid="assessment-stagger"
              />
              <Input
                name="assessmentSlots"
                label="Slots per day"
                type="number"
                inputMode="numeric"
                min={1}
                max={48}
                step={1}
                value={form.slots}
                onChange={(e) => setForm((f) => ({ ...f, slots: e.target.value }))}
                data-testid="assessment-slots"
              />
            </div>
          ) : (
            <Input
              name="assessmentTimes"
              label="Times (24-hour, separated by commas)"
              placeholder="10:00, 10:20"
              value={form.timesText}
              onChange={(e) => setForm((f) => ({ ...f, timesText: e.target.value }))}
              helperText="One slot per time. Customers pick from these."
              data-testid="assessment-times"
            />
          )}

          <div className="ferro-settings__form-grid">
            <Input
              name="assessmentDailyCap"
              label="Assessments per day"
              type="number"
              inputMode="numeric"
              min={1}
              max={500}
              step={1}
              value={form.dailyCap}
              onChange={(e) => setForm((f) => ({ ...f, dailyCap: e.target.value }))}
              helperText="The limit the booking engine enforces."
              data-testid="assessment-daily-cap"
            />
            <Input
              name="assessmentSlotCap"
              label="Per slot (optional)"
              type="number"
              inputMode="numeric"
              min={1}
              max={500}
              step={1}
              value={form.slotCap}
              placeholder="Same as per day"
              onChange={(e) => setForm((f) => ({ ...f, slotCap: e.target.value }))}
              helperText="Shown on the slot list; the daily limit is what binds."
              data-testid="assessment-slot-cap"
            />
            <Input
              name="assessmentPrice"
              label="Price"
              type="text"
              inputMode="decimal"
              prefix="$"
              value={form.price}
              onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
              disabled={!canEditPrice}
              {...(canEditPrice ? {} : { helperText: 'Only the owner can change the price.' })}
              data-testid="assessment-price"
            />
            <Input
              name="assessmentDuration"
              label="Duration (minutes)"
              type="number"
              inputMode="numeric"
              min={5}
              max={480}
              step={5}
              value={form.duration}
              onChange={(e) => setForm((f) => ({ ...f, duration: e.target.value }))}
              data-testid="assessment-duration"
            />
            <Input
              name="assessmentNotice"
              label="Minimum notice (hours)"
              type="number"
              inputMode="decimal"
              min={0}
              step={1}
              value={form.notice}
              placeholder="No minimum"
              onChange={(e) => setForm((f) => ({ ...f, notice: e.target.value }))}
              helperText="24 means a slot can be booked until this time the day before."
              data-testid="assessment-notice"
            />
            <Input
              name="assessmentWindow"
              label="Booking window (days ahead)"
              type="number"
              inputMode="numeric"
              min={1}
              max={730}
              step={1}
              value={form.window}
              placeholder="No limit"
              onChange={(e) => setForm((f) => ({ ...f, window: e.target.value }))}
              helperText="How far ahead customers can book."
              data-testid="assessment-window"
            />
          </div>

          <p className="ferro-settings__preview" data-testid="assessment-preview" aria-live="polite">
            {preview}
          </p>

          {saveError ? (
            <div data-testid="assessment-save-error">
              <Alert variant="error">{saveError}</Alert>
            </div>
          ) : null}
          {saved && !saveError ? (
            <div data-testid="assessment-save-success">
              <Alert variant="success">{describeWritten(saved.written)}</Alert>
              {saved.warnings.length > 0 ? (
                <div data-testid="assessment-warnings">
                  <Alert variant="warning">{saved.warnings.join(' ')}</Alert>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="ferro-settings__actions">
            <Button type="submit" variant="primary" isLoading={isSaving} disabled={!isDirty || isSaving} data-testid="assessment-save">
              Save schedule
            </Button>
            <Button type="button" variant="ghost" onClick={handleReset} disabled={!isDirty || isSaving} data-testid="assessment-reset">
              Reset
            </Button>
          </div>
        </form>

        {schedule.configured ? (
          <div className="ferro-settings__danger-zone" data-testid="assessment-clear-zone">
            {confirmClear ? (
              <>
                <p className="ferro-muted">
                  Removes all {schedule.ruleCount} assessment times. Customers will be able to book daycare and
                  boarding without an assessment until you set a schedule again.
                </p>
                <div className="ferro-capacity__actions">
                  <Button variant="danger" size="sm" isLoading={isClearing} onClick={() => void handleClear()} data-testid="assessment-clear-confirm">
                    Remove all times
                  </Button>
                  <Button variant="ghost" size="sm" disabled={isClearing} onClick={() => setConfirmClear(false)} data-testid="assessment-clear-keep">
                    Keep
                  </Button>
                </div>
              </>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => setConfirmClear(true)} data-testid="assessment-clear">
                Stop offering assessments
              </Button>
            )}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
